
import Vue from 'vue';
import Component from 'vue-class-component';
import MainLayout from '../components/MainLayout.vue';
import SessionListElement from '@/components/SessionListElement.vue';
import SessionsState from '@/store/modules/Sessions';
import UserState from '@/store/modules/User';
import store from '@/store';
import { getModule } from 'vuex-module-decorators';
import NewSessionButton from '../components/NewSessionButton.vue';
import FolderCard from '@/components/FolderCard.vue';
import NewFolderModal from '@/components/NewFolderModal.vue';
import EditFolderModal from '@/components/EditFolderModal.vue';
import Folder from '@/model/Folder';

const sessionsState = getModule(SessionsState, store);
const userState = getModule(UserState, store);

@Component({
    components: {
        MainLayout,
        SessionListElement,
        NewSessionButton,
        FolderCard,
        NewFolderModal,
        EditFolderModal,
    },
    beforeRouteEnter(to, from, next) {
        sessionsState.loadUserSessionList(userState.user!.id);
        next();
    },
    beforeRouteLeave(to, from, next) {
        const vm = this as SessionList;
        const goingToSession = to?.name === 'editSession' || to?.name === 'newSession';
        if (!goingToSession) {
            next();
            return;
        }

        vm.isLeavingSessionListContent = true;
        window.dispatchEvent(new CustomEvent('raws-command-center-leave'));
        window.dispatchEvent(new CustomEvent('raws-sessionlist-leave'));
        window.setTimeout(() => next(), 280);
    },
})
export default class SessionList extends Vue {
    public newFolderModalOpen: boolean = false;
    public editFolderModalOpen: boolean = false;
    public selectedFolder: Folder | null = null;
    public isLeavingSessionListContent = false;
    public isSessionListContentVisible = false;
    public folderTransitioning = false;
    private exitFolderListener?: () => void;

    get sessions() {
        return sessionsState.sortedSessions[userState.user!.id];
    }

    get displayedSessions() {
        if (sessionsState.folderState.isInsideFolder) {
            return sessionsState.filteredSessions[userState.user!.id] || [];
        } else {
            return sessionsState.sessionsNotInFolders[userState.user!.id] || [];
        }
    }

    get userFolders() {
        return sessionsState.folders[userState.user!.id] || [];
    }

    get isInsideFolder() {
        return sessionsState.folderState.isInsideFolder;
    }

    get isLoading() {
        return sessionsState.sessionListLoading;
    }

    public onFolderCreated() {
        // Folder created successfully, no additional action needed
    }

    public onFolderUpdated() {
        // Folder updated successfully, no additional action needed
    }

    public onFolderDeleted() {
        // Folder deleted successfully, no additional action needed
    }

    public onFolderProcessing(value: boolean) {
        this.folderTransitioning = value;
    }

    public openFolder(folder: Folder, event?: MouseEvent) {
        if (this.folderTransitioning) return;

        if (event) {
            window.dispatchEvent(new CustomEvent('raws-ui-ripple', {
                detail: {
                    x: event.clientX,
                    y: event.clientY,
                },
            }));
        }

        this.isLeavingSessionListContent = true;
        this.folderTransitioning = true;
        window.dispatchEvent(new CustomEvent('raws-sessionlist-leave'));

        window.setTimeout(() => {
            sessionsState.enterFolder(folder.id);
            this.isLeavingSessionListContent = false;
            this.folderTransitioning = false;
        }, 280);
    }

    public exitFolder() {
        if (this.folderTransitioning) return;

        this.isLeavingSessionListContent = true;
        this.folderTransitioning = true;
        window.dispatchEvent(new CustomEvent('raws-sessionlist-leave'));

        window.setTimeout(() => {
            sessionsState.exitFolder();
            this.isLeavingSessionListContent = false;
            this.folderTransitioning = false;
        }, 280);
    }

    public editFolder(folder: Folder) {
        this.selectedFolder = folder;
        this.editFolderModalOpen = true;
    }

    public async deleteFolder(folder: Folder) {
        if (userState.user) {
            this.folderTransitioning = true;
            try {
                await this.$nextTick();
                await sessionsState.deleteFolder({
                    userId: userState.user.id,
                    folderId: folder.id,
                });
            } finally {
                this.folderTransitioning = false;
            }
        }
    }

    public editCurrentFolder() {
        const currentFolder = sessionsState.currentFolder;
        if (currentFolder) {
            this.selectedFolder = currentFolder;
            this.editFolderModalOpen = true;
        }
    }

    public mounted() {
        this.exitFolderListener = () => this.exitFolder();
        window.addEventListener('raws-folder-exit', this.exitFolderListener);
        window.setTimeout(() => {
            this.isSessionListContentVisible = true;
        }, 20);
    }

    public beforeDestroy() {
        if (this.exitFolderListener) {
            window.removeEventListener('raws-folder-exit', this.exitFolderListener);
        }
    }
}
